import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import { SortAsc } from "lucide-react";

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const EmployeeLeaveTable = ({
  employeeId,
  workingDate,
  disableDeleteIds = [],
  onRefresh,
}) => {
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(false);

  const MASTER_LEAVE_TYPES = [
    "Casual Leave",
    "Compensatory off",
    "E.S.I leave",
    "Earn Leave",
    "Maternity Leave(Female Executive)",
    "Restricted Leave",
    "Sick leave",
    "Without Wages",
  ];
  /* ---------------- FETCH LEAVES ---------------- */
  const fetchLeaves = async () => {
    if (!employeeId || !workingDate) return;

    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/leave-applications`, {
        headers: getAuthHeaders(),
        params: {
          employee_id: employeeId,
          working_date: workingDate,
          is_report: true,
        },
      });

      setLeaveData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch leave error", err);
      setLeaveData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, [employeeId, workingDate]);
  const handleDelete = async (row) => {
    const { id, leave_type, source } = row;

    if (disableDeleteIds.includes(id)) return;

    const ok = window.confirm(
      "Are you sure you want to delete this leave request?",
    );
    if (!ok) return;

    try {
      if (MASTER_LEAVE_TYPES.includes(leave_type)) {
        await axios.put(
          `${BASE_URL}/api/leave-applications-report/${id}`,
          {},
          { headers: getAuthHeaders() },
        );
      }
      else if (source === "application") {
        await axios.delete(
          `${BASE_URL}/api/leave-applications/${id}?source=${source}`,
          {
            headers: getAuthHeaders(),
          },
        );
      }
      else {
        await axios.delete(`${BASE_URL}/api/leave-applications/${id}`, {
          headers: getAuthHeaders(),
        });
      }

      alert("Leave deleted successfully");
      fetchLeaves();
      onRefresh?.();
    } catch (err) {
      console.error("Delete error", err);
      alert(err.response?.data?.message || "Failed to delete leave");
    }
  };

  if (loading) {
    return <p className="text-center py-4">Loading leave data...</p>;
  }

  if (!leaveData.length) {
    return (
      <p className="text-center text-gray-500 py-4">No leave records found</p>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <div className="overflow-x-auto border rounded">
      <table className="min-w-full text-xs border-collapse text-center">
        <thead className="bg-blue-500 text-white">
          <tr>
            <th className="border px-2 py-2">S.No</th>
            <th className="border px-2 py-2">Emp ID</th>
            <th className="border px-2 py-2">Employee</th>
            <th className="border px-2 py-2">Division</th>
            <th className="border px-2 py-2">DOJ</th>
            <th className="border px-2 py-2">DOR</th>
            <th className="border px-2 py-2">State</th>
            <th className="border px-2 py-2">City</th>
            <th className="border px-2 py-2">Type</th>
            <th className="border px-2 py-2">Emp Remarks</th>
            <th className="border px-2 py-2">Start Date</th>
            <th className="border px-2 py-2">End Date</th>
            <th className="border px-2 py-2">Duration</th>
            <th className="border px-2 py-2">Manager</th>
            <th className="border px-2 py-2">HR Status</th>
            <th className="border px-2 py-2">HR Remarks</th>
            <th className="border px-2 py-2">Remove</th>
          </tr>
        </thead>

        <tbody>
          {leaveData.map((row, index) => {
            const disableDelete = disableDeleteIds.includes(row.id);

            return (
              <tr key={row.id} className="even:bg-gray-50 hover:bg-blue-50">
                <td className="border px-2 py-1">{index + 1}</td>

                <td className="border px-2 py-1">{row.employee}</td>
                <td className="border px-2 py-1">{row.employee_name}</td>
                <td className="border px-2 py-1">{row.division}</td>
                <td className="border px-2 py-1">{formatDate(row.doj)}</td>
                <td className="border px-2 py-1">{formatDate(row.dor)}</td>
                <td className="border px-2 py-1">{row.state}</td>
                <td className="border px-2 py-1">{row.city}</td>
                <td className="border px-2 py-1">{row.leave_type}</td>
                <td className="border px-2 py-1 text-left">
                  {row.empremarks || "-"}
                </td>
                <td className="border px-2 py-1">
                  {formatDate(row.start_date)}
                </td>
                <td className="border px-2 py-1">{formatDate(row.end_date)}</td>
                <td className="border px-2 py-1">{row.duration || "-"}</td>
                <td className="border px-2 py-1">{row.manager}</td>
                <td className="border px-2 py-1">{row.hr_status || "-"}</td>
                <td className="border px-2 py-1">{row.hr_remarks || "-"}</td>
                <td className="border px-2 py-1">
                  <button
                    disabled={disableDelete}
                    onClick={() => handleDelete(row)}
                    className={`px-3 py-1 rounded text-white ${
                      disableDelete
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EmployeeLeaveTable;
